import { useState, useCallback, useEffect, useRef } from "react"
import type { MouseEvent } from "react"

const STORAGE_KEY_PREFIX = "view-sheet-width"
const DEFAULT_WIDTH = 600
const MIN_WIDTH = 400
const EXPANDED_WIDTH_RATIO = 0.9

function clampToViewport(width: number): number {
  return Math.min(width, window.innerWidth * EXPANDED_WIDTH_RATIO)
}

function getStoredWidth(storageKey: string, defaultWidth: number): number {
  try {
    const stored = localStorage.getItem(storageKey)
    if (stored) {
      const parsed = Number(stored)
      if (!Number.isNaN(parsed) && parsed >= MIN_WIDTH) {
        return clampToViewport(parsed)
      }
    }
  } catch {
    // ignore
  }
  return clampToViewport(defaultWidth)
}

export function useViewSheetResize(id: string, defaultWidth = DEFAULT_WIDTH) {
  const storageKey = `${STORAGE_KEY_PREFIX}:${id}`
  const [width, setWidth] = useState(() =>
    getStoredWidth(storageKey, defaultWidth),
  )
  const [isExpanded, setIsExpanded] = useState(false)
  const widthRef = useRef(width)
  const preExpandWidthRef = useRef(width)

  useEffect(() => {
    widthRef.current = width
  }, [width])

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, String(Math.round(width)))
    } catch {
      // ignore
    }
  }, [width, storageKey])

  useEffect(() => {
    return () => {
      document.documentElement.classList.remove("view-sheet-resizing")
    }
  }, [])

  const onResizeStart = useCallback((event: MouseEvent) => {
    event.preventDefault()
    const startX = event.clientX
    const startWidth = widthRef.current

    document.documentElement.classList.add("view-sheet-resizing")

    function handleMouseMove(e: globalThis.MouseEvent) {
      const delta = startX - e.clientX
      const newWidth = startWidth + delta
      const maxWidth = window.innerWidth * EXPANDED_WIDTH_RATIO
      setWidth(Math.max(MIN_WIDTH, Math.min(maxWidth, newWidth)))
    }

    function handleMouseUp() {
      document.documentElement.classList.remove("view-sheet-resizing")
      document.removeEventListener("mousemove", handleMouseMove)
      document.removeEventListener("mouseup", handleMouseUp)
      setIsExpanded(false)
    }

    document.addEventListener("mousemove", handleMouseMove)
    document.addEventListener("mouseup", handleMouseUp)
  }, [])

  const onResetWidth = useCallback(() => {
    setWidth(clampToViewport(defaultWidth))
    setIsExpanded(false)
  }, [defaultWidth])

  const onToggleExpand = useCallback(() => {
    setIsExpanded((prev) => {
      if (prev) {
        setWidth(preExpandWidthRef.current)
        return false
      }
      preExpandWidthRef.current = widthRef.current
      setWidth(window.innerWidth * EXPANDED_WIDTH_RATIO)
      return true
    })
  }, [])

  return { width, isExpanded, onResizeStart, onResetWidth, onToggleExpand }
}
